// SuccessStories — Public page listing published success stories.
// Route: /success-stories
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import * as Lucide from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const RED = "#C8102E";
const GOLD = "#D4AF37";

const TYPES = [
  { key: "", label: "All Stories" },
  { key: "entrepreneur", label: "Entrepreneur" },
  { key: "affiliate", label: "Affiliate" },
  { key: "agent", label: "Agent" },
  { key: "stockist", label: "Stockist" },
  { key: "business_owner", label: "Business Owner" },
  { key: "community", label: "Community" },
];

export default function SuccessStories() {
  const [items, setItems] = useState([]);
  const [type, setType] = useState("");
  useEffect(() => {
    const q = type ? `?story_type=${type}` : "";
    axios.get(`${BACKEND}/api/public/success-stories${q}`).then((r) => setItems(r.data || [])).catch(() => {});
  }, [type]);

  return (
    <div className="min-h-screen bg-white text-[#09090b]">
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 h-14 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold" data-testid="ss-back"><Lucide.ArrowLeft className="w-4 h-4" /> Home</Link>
          <div className="text-xs uppercase tracking-widest font-semibold text-zinc-500">Success Stories</div>
          <div className="w-10" />
        </div>
      </header>

      <section className="border-b border-zinc-200" style={{ background: "#FAFAFA" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-12 lg:py-16">
          <div className="tag mb-3"><span className="w-1.5 h-1.5 inline-block" style={{ background: RED }} /> Real people. Real income.</div>
          <h1 className="font-display font-black text-3xl sm:text-5xl tracking-tight leading-[1.05]" data-testid="ss-title">MIRESCO <span style={{ color: RED }}>Success Stories</span></h1>
          <p className="mt-3 text-zinc-600 max-w-2xl">Malaysians building income and businesses through MIRESCO. Their story could be yours.</p>
          <div className="mt-6 flex flex-wrap gap-2" data-testid="ss-filter">
            {TYPES.map((t) => (
              <button key={t.key} onClick={() => setType(t.key)} className={`text-xs uppercase tracking-widest font-semibold px-3 py-1.5 border transition-colors ${type === t.key ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 hover:border-zinc-900"}`} data-testid={`ss-type-${t.key || "all"}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-10 lg:py-14">
        {items.length === 0 ? (
          <div className="border-2 border-dashed border-zinc-300 p-12 text-center text-sm text-zinc-500">
            No success stories yet. Check back soon — the first cohort is being written.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="ss-grid">
            {items.map((s) => (
              <article key={s.story_id} className="bg-white border border-zinc-200 hover:border-zinc-900 hover:shadow-[6px_6px_0_0_#09090b] transition-all overflow-hidden" data-testid={`ss-card-${s.slug}`}>
                <div className="aspect-video bg-zinc-100 relative">
                  {s.video_url ? (
                    <video src={s.video_url} controls preload="metadata" className="w-full h-full object-cover" />
                  ) : s.hero_image_url ? (
                    <img src={s.hero_image_url} alt={s.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-white text-lg font-black" style={{ background: `linear-gradient(135deg, ${RED} 0%, #7c0a1e 100%)` }}>{s.name || "MIRESCO"}</div>
                  )}
                  {s.monthly_income_myr > 0 && (
                    <span className="absolute top-2 left-2 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 text-white" style={{ background: GOLD, color: "#1A1A1A" }}>
                      RM {Number(s.monthly_income_myr).toLocaleString()}/mo
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{s.story_type || "story"}</div>
                  <h3 className="font-display font-black text-lg tracking-tight mt-1 leading-tight">{s.title}</h3>
                  {s.quote && <p className="mt-2 text-sm text-zinc-700 italic line-clamp-3">&ldquo;{s.quote}&rdquo;</p>}
                  <div className="mt-3 text-xs text-zinc-500">
                    <span className="font-semibold text-zinc-900">{s.name}</span>
                    {s.role ? ` · ${s.role}` : ""}
                    {s.location ? ` · ${s.location}` : ""}
                  </div>
                  {s.opportunity_slug && (
                    <Link to={`/opportunity/${s.opportunity_slug}`} className="mt-3 inline-flex items-center gap-1 text-xs font-bold hover:opacity-80" style={{ color: RED }} data-testid={`ss-opp-link-${s.slug}`}>
                      Join this opportunity <Lucide.ArrowRight className="w-3 h-3" />
                    </Link>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
